import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { categories, allComics, hotComics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [scrollToTopVisible, setScrollToTopVisible] = useState(false);
  const [sortBy, setSortBy] = useState<'latest' | 'hot' | 'rating'>('latest');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollToTopVisible(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const currentCategory = slug === 'all'
    ? { name: '全部漫画', slug: 'all', icon: 'ri-book-open-line', count: allComics.length }
    : categories.find((c) => c.slug === slug) || { name: '全部漫画', slug: 'all', icon: 'ri-book-open-line', count: allComics.length };

  const filteredComics: Comic[] = slug === 'all'
    ? allComics
    : allComics.filter((c) => c.categorySlug === slug || c.category === currentCategory.name);

  const sortedComics = [...filteredComics].sort((a, b) => {
    if (sortBy === 'hot') return parseFloat(b.views.replace('万', '')) - parseFloat(a.views.replace('万', ''));
    if (sortBy === 'rating') return b.rating - a.rating;
    return new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime();
  });

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100/70 border-b border-background-200/60">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3">
            <nav aria-label="面包屑导航" className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
              <span className="text-foreground-800 font-medium">{currentCategory.name}</span>
            </nav>
          </div>
        </div>

        {/* Category Header */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100">
                <i className={`${currentCategory.icon} text-lg text-accent-600`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500">
                  共 {filteredComics.length} 部漫画
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="pb-4 bg-background-50 sticky top-16 z-30 border-b border-background-200/60">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex items-center gap-1.5 overflow-x-auto flex-wrap">
            <Link
              to="/category/all"
              className={`px-4 py-2 text-sm rounded-full transition-all whitespace-nowrap cursor-pointer ${
                slug === 'all'
                  ? 'bg-primary-500 text-white dark:text-foreground-950 font-medium'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              全部
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm rounded-full transition-all whitespace-nowrap cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-white dark:text-foreground-950 font-medium'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Sort Controls */}
        <section className="py-4 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex items-center gap-3">
            <span className="text-sm text-foreground-500">排序：</span>
            {[
              { key: 'latest' as const, label: '最新' },
              { key: 'hot' as const, label: '最热' },
              { key: 'rating' as const, label: '评分' },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setSortBy(s.key)}
                className={`px-4 py-1.5 text-sm rounded-full transition-all whitespace-nowrap cursor-pointer ${
                  sortBy === s.key
                    ? 'bg-secondary-500 text-background-50 dark:text-foreground-950 font-medium'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </section>

        {/* Comic Grid */}
        <section className="py-6 md:py-10 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            {sortedComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {sortedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} size="medium" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-book-open-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      {scrollToTopVisible && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-40 w-11 h-11 flex items-center justify-center rounded-full bg-primary-500 text-white dark:text-foreground-950 shadow-lg hover:bg-primary-600 transition-all cursor-pointer"
          aria-label="回到顶部"
        >
          <i className="ri-arrow-up-line text-lg"></i>
        </button>
      )}
    </div>
  );
}